/**
 * Party Card Component
 * 
 * Displays customer or supplier information in a card format with quick edit capabilities.
 * Shows: Basic info, addresses, notification preferences, financial info
 * 
 * Features:
 * - Quick edit for addresses
 * - Quick edit for notification preferences
 * - Visual status indicators
 * - Action buttons for common operations
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  Stack,
  Tooltip,
  Divider,
  Badge,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as LedgerIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  LocationOn as LocationIcon,
  Notifications as NotificationsIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  AccountBalance as BrokerIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
} from '@mui/icons-material';

interface Address {
  id?: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  is_default?: boolean;
}

interface NotificationPreferences {
  email_enabled: boolean;
  sms_enabled: boolean;
  whatsapp_enabled: boolean;
  notification_types: {
    order_updates: boolean;
    payment_reminders: boolean;
    promotional: boolean;
  };
}

interface Party {
  id: string;
  name: string;
  email?: string;
  mobile_no?: string;
  phone?: string;
  customer_type?: string;
  billing_address?: Address;
  shipping_addresses?: Address[];
  communication_preferences?: NotificationPreferences;
  credit_limit?: number;
  outstanding_balance?: number;
  payment_terms?: number;
  is_active: boolean;
  type: 'customer' | 'supplier';
}

interface PartyCardProps {
  party: Party;
  onEdit: (party: Party) => void;
  onDelete: (id: string) => void;
  onViewLedger: (party: Party) => void;
  onEditAddress: (party: Party, addressType: 'billing' | 'shipping') => void;
  onEditNotifications: (party: Party) => void;
  onAddShippingAddress?: (party: Party) => void;
}

const PartyCard: React.FC<PartyCardProps> = ({
  party,
  onEdit,
  onDelete,
  onViewLedger,
  onEditAddress,
  onEditNotifications,
  onAddShippingAddress,
}) => {
  const [expanded, setExpanded] = useState(false);

  /**
   * Get customer type icon
   */
  const getTypeIcon = () => {
    if (party.type === 'supplier') {
      return <BusinessIcon sx={{ color: 'secondary.main' }} />;
    }

    switch (party.customer_type) {
      case 'RETAIL':
        return <StoreIcon sx={{ color: 'primary.main' }} />;
      case 'RESELLER':
        return <PersonIcon sx={{ color: 'info.main' }} />;
      case 'WHOLESALER':
        return <BusinessIcon sx={{ color: 'secondary.main' }} />;
      case 'BROKER':
        return <BrokerIcon sx={{ color: 'warning.main' }} />;
      default:
        return <PersonIcon />;
    }
  };

  /**
   * Get customer type label
   */
  const getTypeLabel = () => {
    if (party.type === 'supplier') return 'Supplier';
    
    switch (party.customer_type) {
      case 'RETAIL':
        return 'Retail';
      case 'RESELLER':
        return 'Reseller';
      case 'WHOLESALER':
        return 'Wholesaler';
      case 'BROKER':
        return 'Broker';
      default:
        return party.customer_type;
    }
  };

  /**
   * Format address for display
   */
  const formatAddress = (address?: Address) => {
    if (!address) return 'No address';
    
    const parts = [
      address.address_line1,
      address.address_line2,
      address.city,
      address.state,
      address.pincode,
    ].filter(Boolean);
    
    return parts.join(', ');
  };

  /**
   * Format currency
   */
  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const notificationPrefs = party.communication_preferences;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        {/* Header with Name and Type */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            {getTypeIcon()}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>
                {party.name}
              </Typography>
              <Chip
                label={getTypeLabel()}
                size="small"
                color={party.type === 'supplier' ? 'secondary' : 'primary'}
                sx={{ borderRadius: 4, height: 20, fontSize: '0.7rem' }}
              />
            </Box>
          </Box>
          <Chip
            label={party.is_active ? 'Active' : 'Inactive'}
            size="small"
            color={party.is_active ? 'success' : 'default'}
            sx={{ borderRadius: 4 }}
          />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Contact Information */}
        <Stack spacing={1} sx={{ mb: 2 }}>
          {party.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                {party.email}
              </Typography>
            </Box>
          )}
          {party.mobile_no && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                {party.mobile_no}
              </Typography>
            </Box>
          )}
        </Stack>

        {/* Billing Address Section */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationIcon sx={{ fontSize: 16 }} />
              {party.type === 'customer' ? 'Billing Address' : 'Address'}
            </Typography>
            <IconButton
              size="small"
              onClick={() => onEditAddress(party, 'billing')}
              sx={{ padding: 0.5 }}
              aria-label="Edit address"
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
            {formatAddress(party.billing_address)}
          </Typography>
        </Box>

        {/* Shipping Addresses (Customers only) */}
        {party.type === 'customer' && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationIcon sx={{ fontSize: 16 }} />
                Shipping Addresses
                <Badge badgeContent={party.shipping_addresses?.length || 0} color="primary" sx={{ ml: 1 }} />
              </Typography>
              <Stack direction="row" spacing={0.5}>
                {onAddShippingAddress && (
                  <IconButton
                    size="small"
                    onClick={() => onAddShippingAddress(party)}
                    sx={{ padding: 0.5 }}
                    aria-label="Add shipping address"
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  onClick={() => onEditAddress(party, 'shipping')}
                  sx={{ padding: 0.5 }}
                  aria-label="Edit shipping addresses"
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            </Box>
            {party.shipping_addresses && party.shipping_addresses.length > 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                {party.shipping_addresses.length} address{party.shipping_addresses.length > 1 ? 'es' : ''} configured
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                No shipping addresses
              </Typography>
            )}
          </Box>
        )}

        {/* Notification Preferences */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <NotificationsIcon sx={{ fontSize: 16 }} />
              Notifications
            </Typography>
            <IconButton
              size="small"
              onClick={() => onEditNotifications(party)}
              sx={{ padding: 0.5 }}
              aria-label="Edit notifications"
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={notificationPrefs?.email_enabled ? 'Email enabled' : 'Email disabled'}>
              <EmailIcon
                sx={{
                  fontSize: 20,
                  color: notificationPrefs?.email_enabled ? 'success.main' : 'text.disabled',
                }}
              />
            </Tooltip>
            <Tooltip title={notificationPrefs?.sms_enabled ? 'SMS enabled' : 'SMS disabled'}>
              <PhoneIcon
                sx={{
                  fontSize: 20,
                  color: notificationPrefs?.sms_enabled ? 'success.main' : 'text.disabled',
                }}
              />
            </Tooltip>
            <Tooltip title={notificationPrefs?.whatsapp_enabled ? 'WhatsApp enabled' : 'WhatsApp disabled'}>
              <WhatsAppIcon
                sx={{
                  fontSize: 20,
                  color: notificationPrefs?.whatsapp_enabled ? 'success.main' : 'text.disabled',
                }}
              />
            </Tooltip>
          </Stack>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Financial Information */}
        <Box>
          {party.type === 'customer' ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  Credit Limit:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                  {formatCurrency(party.credit_limit)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  Outstanding:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    color: (party.outstanding_balance || 0) > 0 ? 'error.main' : 'success.main',
                  }}
                >
                  {formatCurrency(party.outstanding_balance)}
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                Payment Terms:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                {party.payment_terms || 0} days
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>

      {/* Action Buttons */}
      <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<LedgerIcon />}
          onClick={() => onViewLedger(party)}
          sx={{ textTransform: 'none', fontSize: '0.8rem', flex: 1 }}
        >
          Ledger
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => onEdit(party)}
          sx={{ textTransform: 'none', fontSize: '0.8rem', flex: 1 }}
        >
          Edit
        </Button>
        <IconButton
          size="small"
          color="error"
          onClick={() => onDelete(party.id)}
          aria-label="Delete party"
        >
          <DeleteIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </CardActions>
    </Card>
  );
};

export default PartyCard;
